import React, { useState, useMemo, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight, faXmark } from "@fortawesome/sharp-solid-svg-icons";
import { cn } from "@/shared/utils";
import { useMediaQuery } from "@react-hookz/web";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { OwnerSchema, UnderwriterConfigKey } from "@/shared/types/Quote.interface";
import Strings from "@/shared/utils/Strings.constants";
import { FormField } from "./FormField";
import { InputWithLabel } from "./ui/InputWithLabel";
import { Button } from "./ui/Button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { ResponsiveModalSheet } from "./ResponsiveModalSheet";
import { PetUnderwriterType } from "@spotpetinsurance/spot-types/entities/PetQuote";
import { useQueryClient } from "@tanstack/react-query";
import { EmailUtils } from "@/shared/utils/EmailUtils";
import { AnalyticsUtils } from "../utils/AnalyticsUtils";
import { UIUtils } from "../utils/UIUtils";
import { PublicConfig } from "../PublicConfig";
import { CookieUtils } from "../utils/CookieUtils";

interface QuoteSuggestionModalProps {
    underwriter: PetUnderwriterType;
}

const QuoteSuggestionModal = ({ underwriter }: QuoteSuggestionModalProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const isMobile = useMediaQuery("(max-width: 767px)");
    const queryClient = useQueryClient();
    const emailUtils = useMemo(() => new EmailUtils(underwriter, queryClient), [queryClient, underwriter]);

    const { email, ratingZipcode } = OwnerSchema.shape;
    const formSchema = z.object({ email, ratingZipcode });
    type FormData = z.infer<typeof formSchema>;

    const { control, watch, handleSubmit } = useForm<FormData>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            email: "",
            ratingZipcode: ""
        }
    });

    const handleOpen = () => {
        setIsModalOpen(true);
        setErrorMessage(null);
    };

    const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

    useEffect(() => {
        if (redirectUrl) {
            window.location.href = redirectUrl;
        }
    }, [redirectUrl]);

    const onSubmit = async (data: FormData) => {
        setErrorMessage(null);
        setIsLoading(true);

        try {
            const result = await emailUtils.checkForExistingQuote(data.email, data.ratingZipcode);

            if (result.quoteId) {
                const segmentPayload = {
                    extra: {
                        quote: {
                            quoteId: result.quoteId,
                            email: data.email,
                            ratingAddress: {
                                zipCode: data.ratingZipcode
                            }
                        }
                    }
                };
                AnalyticsUtils.trackSegmentEvent("Quote Retrieved", segmentPayload);

                const uwConfigKey = underwriter.replace("-", "_").toUpperCase() as UnderwriterConfigKey;
                const cookieId = PublicConfig?.[uwConfigKey]?.COOKIE_QUOTE_ID_KEY;
                CookieUtils.set(cookieId, result.quoteId, { expires: 365 });

                const url = new URL(window.location.href);
                const searchParams = new URLSearchParams(url.search);
                searchParams.set("quoteId", result.quoteId);

                // Preserve the base URL and pathname, just update the search params
                url.search = searchParams.toString();
                setRedirectUrl(url.toString());
            } else {
                setIsLoading(false);
                setErrorMessage("No quote found for this email and zipcode, please try again.");
            }
        } catch (error) {
            setIsLoading(false);
            setErrorMessage("An error occurred while checking for your quote. Please try again.");
        }
    };

    const buttonClass = cn("mt-4 flex w-full items-center justify-center gap-2 rounded-full px-4 py-3 text-content-primary-invert");

    const triggerButtonProps = {
        variant: "outline-secondary" as const,
        endDecorator: <FontAwesomeIcon icon={faChevronRight} />,
        size: "sm" as const,
        className: "w-[200px] sm:w-[190px] rounded-full md:py-4 bg-background-primary text-xs md:text-base"
    };

    const content = (
        <div className="flex flex-col" data-testid={isMobile ? "retrieve-quote-sheet" : "retrieve-quote-modal"}>
            <div className="mb-2 text-lg font-bold md:text-xl">Welcome back!</div>
            <p className="md:text-md mb-5 text-base">Enter the email and zip code you used and we&apos;ll retrieve your saved quote</p>
            <div className="mb-4 flex flex-col gap-5">
                <Controller
                    name="email"
                    control={control}
                    render={({ field: { ref, onChange, ...rest }, fieldState }) => (
                        <FormField className="flex-1" error={fieldState?.error?.message} errorId="error-email">
                            <InputWithLabel
                                label={Strings.EMAIL_ADDRESS}
                                variant="floating"
                                inputRef={ref}
                                error={fieldState?.error?.message}
                                inputTestId="retrieve-quote-email"
                                inputProps={{
                                    ...rest,
                                    onChange: e => {
                                        onChange(e);
                                        setErrorMessage(null);
                                    },
                                    type: "email",
                                    autoComplete: "email",
                                    placeholder: " ",
                                    "aria-describedby": fieldState?.error ? "error-email" : undefined
                                }}
                            />
                        </FormField>
                    )}
                />
                <Controller
                    name="ratingZipcode"
                    control={control}
                    render={({ field: { ref, value, onChange, ...rest }, fieldState }) => (
                        <FormField className="flex-1" error={fieldState?.error?.message} errorId="error-ratingZipcode">
                            <InputWithLabel
                                label={Strings.ZIP_CODE}
                                variant="floating"
                                inputRef={ref}
                                error={fieldState?.error?.message}
                                inputTestId="retrieve-quote-zipcode"
                                inputProps={{
                                    ...rest,
                                    onChange: e => {
                                        onChange(e);
                                        setErrorMessage(null);
                                    },
                                    autoComplete: "postal-code",
                                    placeholder: " ",
                                    "aria-describedby": fieldState?.error ? "error-ratingZipcode" : undefined,
                                    value: UIUtils.maskPostalCodeUS(value)
                                }}
                            />
                        </FormField>
                    )}
                />
            </div>
            {errorMessage && (
                <p className="mb-4 text-sm text-content-danger" role="alert">
                    {errorMessage}
                </p>
            )}
            <Button endDecorator={<FontAwesomeIcon icon={faChevronRight} />} className={buttonClass} onClick={handleSubmit(onSubmit)} disabled={isLoading} loading={isLoading}>
                Get quote
            </Button>
            {isMobile ? (
                <Button className="mt-3 gap-2" variant="ghost" onClick={() => setIsModalOpen(false)}>
                    <FontAwesomeIcon icon={faXmark} /> Close
                </Button>
            ) : null}
        </div>
    );

    return (
        <div className="flex items-center justify-between gap-8 p-3 text-content-primary md:justify-center md:gap-4 md:p-4">
            <p className="text-sm font-bold md:text-lg">Looks like you already have a quote.</p>
            {isMobile ? (
                <Sheet open={isModalOpen} onOpenChange={setIsModalOpen}>
                    <SheetTrigger asChild>
                        <Button {...triggerButtonProps} onClick={handleOpen}>
                            {Strings.GET_SAVED_QUOTE}
                        </Button>
                    </SheetTrigger>
                    <SheetContent side="bottom" className="p-4">
                        {content}
                    </SheetContent>
                </Sheet>
            ) : (
                <ResponsiveModalSheet
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    triggerButton={
                        <Button {...triggerButtonProps} onClick={handleOpen}>
                            {Strings.GET_SAVED_QUOTE}
                        </Button>
                    }
                    styles={{
                        modal: {
                            content: "flex flex-col !max-w-[428px]",
                            title: "flex justify-between items-center",
                            closeButton: "hidden"
                        }
                    }}
                >
                    {content}
                </ResponsiveModalSheet>
            )}
        </div>
    );
};

export default QuoteSuggestionModal;
